import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {testIdProps} from '@github-ui/test-id-props'
import {DotFillIcon, DotIcon, StopIcon} from '@primer/octicons-react'
import {ActionList, NavList} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {memo, useCallback, useMemo} from 'react'

import type {ClientMemexWorkflow} from '../../api/workflows/contracts'
import {useAutoAssignAgentEnabled} from '../../features/auto-assign-agent/use-auto-assign-agent-enabled'
import {
  isAutoAssignAgentWorkflow,
  isLastWorkflowOfType,
  isWorkflowPersisted,
  workflowTypeAsIdentity,
} from '../../helpers/workflow-utilities'
import {validateWorkflow} from '../../helpers/workflow-validation'
import {useProjectRouteParams} from '../../router/use-project-route-params'
import {PROJECT_WORKFLOW_CLIENT_ID_ROUTE} from '../../routes'
import {useWorkflows} from '../../state-providers/workflows/use-workflows'
import {WorkflowResources} from '../../strings'
import {NavLinkActionListItem} from '../react-router/action-list-nav-link-item'
import styles from './automation-side-nav.module.css'
import {WorkflowMenu} from './workflow-menu'

type AutomationSideNavProps = {
  activeWorkflow: ClientMemexWorkflow
}

export const AutomationSideNav = memo(({activeWorkflow}: AutomationSideNavProps) => {
  const {
    workflows,
    workflowWithTriggerTypeIsEnableable,
    setWorkflowMenuState,
    resetWorkflowMenuStates,
    isWorkflowMenuOpen,
    getWorkflowMenuItemsMap,
  } = useWorkflows()

  const autoAssignAgentEnabled = useAutoAssignAgentEnabled()

  const onNavItemClickHandler = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, workflowClientId: ClientMemexWorkflow['clientId']) => {
      // Pretty janky but there's no other way to add options to NavItems
      // This will prevent the href redirect, and open the menu if you click on the
      // SVG or the button.
      if (['button', 'svg', 'path'].includes((e.target as HTMLElement).tagName.toLowerCase())) {
        e.preventDefault()
        setWorkflowMenuState(workflowClientId)
      } else {
        resetWorkflowMenuStates()
      }
    },
    [resetWorkflowMenuStates, setWorkflowMenuState],
  )

  const routeParams = useProjectRouteParams()

  const {agentWorkflows, defaultWorkflows} = useMemo(() => {
    const agent: Array<ClientMemexWorkflow> = []
    const defaults: Array<ClientMemexWorkflow> = []
    for (const workflow of workflows) {
      if (isAutoAssignAgentWorkflow(workflow)) {
        agent.push(workflow)
      } else {
        defaults.push(workflow)
      }
    }
    return {agentWorkflows: agent, defaultWorkflows: defaults}
  }, [workflows])

  const renderWorkflowItem = (workflow: ClientMemexWorkflow) => {
    const enableable = workflowWithTriggerTypeIsEnableable(workflow.triggerType)
    const isActive = workflow.clientId === activeWorkflow.clientId
    const isNonPersistedUserWorkflow = workflow.isUserWorkflow && !isWorkflowPersisted(workflow)
    return (
      <NavLinkActionListItem
        to={{
          pathname: PROJECT_WORKFLOW_CLIENT_ID_ROUTE.generatePath({
            ...routeParams,
            workflowClientId: workflow.clientId,
          }),
        }}
        onClick={e => onNavItemClickHandler(e, workflow.clientId)}
        tabIndex={enableable ? 0 : -1}
        isActive={isActive}
        key={workflow.clientId}
        className={clsx(styles.workflowNavItem, enableable && styles.enableableWorkflowNavItem)}
        {...testIdProps(`workflow-nav-item-${workflowTypeAsIdentity(workflow)}`)}
      >
        <div className={styles.workflowNavItemContent}>
          <div className={styles.workflowNavItemMain}>
            <ProjectWorkflowIcon
              workflow={workflow}
              isActive={isActive}
              isNonPersistedUserWorkflow={isNonPersistedUserWorkflow}
            />
            <WorkflowName name={workflow.name} isNonPersistedUserWorkflow={isNonPersistedUserWorkflow} />
          </div>
          <div className={styles.workflowNavItemMenu}>
            {isWorkflowPersisted(workflow) && (
              <WorkflowMenu
                key={`workflow-nav-list-item-${workflow.clientId}`}
                ref={(node: HTMLButtonElement) => {
                  const map = getWorkflowMenuItemsMap()
                  if (node) {
                    map.set(workflow.clientId, node)
                  } else {
                    map.delete(workflow.clientId)
                  }
                }}
                workflow={workflow}
                showDelete={!isLastWorkflowOfType(workflows, workflow)}
                isOpen={isWorkflowMenuOpen(workflow.clientId)}
              />
            )}
          </div>
        </div>
      </NavLinkActionListItem>
    )
  }

  return (
    <NavList {...testIdProps('settings-side-nav')}>
      {autoAssignAgentEnabled && agentWorkflows.length > 0 && (
        <NavList.Group>
          <ActionList.GroupHeading as="h2">
            {WorkflowResources.agentWorkflowsGroupLabel}
            <BetaLabel className={styles.previewBadge} />
          </ActionList.GroupHeading>
          {agentWorkflows.map(renderWorkflowItem)}
        </NavList.Group>
      )}
      <NavList.Group>
        {/* Override default h3 heading
            see: https://github.com/primer/react/blob/f1c18250b807e247d03b8ea8a6b4954b819d18e3/src/ActionList/Group.tsx#L58
        */}
        <ActionList.GroupHeading as="h2">{WorkflowResources.defaultWorkflowsGroupLabel}</ActionList.GroupHeading>
        {defaultWorkflows.map(renderWorkflowItem)}
      </NavList.Group>
    </NavList>
  )
})

AutomationSideNav.displayName = 'AutomationSideNav'

type ProjectWorkflowIconProps = {
  workflow: ClientMemexWorkflow
  isActive: boolean
  isNonPersistedUserWorkflow: boolean
}

const ProjectWorkflowIcon: React.FC<ProjectWorkflowIconProps> = ({workflow, isActive, isNonPersistedUserWorkflow}) => {
  if (isNonPersistedUserWorkflow) {
    return <Octicon icon={DotFillIcon} size={20} className={styles.userWorkflowIcon} />
  }
  if (workflow.enabled) {
    return <Octicon icon={DotFillIcon} size={20} className={styles.enabledWorkflowIcon} />
  } else if (validateWorkflow(workflow).isValid) {
    return (
      <Octicon
        icon={DotIcon}
        size={20}
        className={clsx(styles.validDisabledWorkflowIcon, isActive && styles.activeWorkflowIcon)}
      />
    )
  } else {
    return <Octicon icon={StopIcon} size={14} className={styles.invalidWorkflowIcon} />
  }
}

type WorkflowNameProps = {
  name: string
  isNonPersistedUserWorkflow: boolean
}

const WorkflowName: React.FC<WorkflowNameProps> = ({name, isNonPersistedUserWorkflow}) => {
  if (isNonPersistedUserWorkflow) {
    return <span className={styles.userWorkflowName}>{name}</span>
  }
  return <span>{name}</span>
}
